import { Filter } from '@tamagui/lucide-icons';
import { Form } from '@unistocks/components/form/form';
import { WatchValues } from '@unistocks/components/form/components/watch-values';
import { createDateSchema, createNumberSchema, oneOf } from '@unistocks/components/form/validators';
import { TransactionFilter } from '@unistocks/features/portfolios/transactions/transaction-filter';
import { t } from '@unistocks/localization';
import { translateEnumOptions } from '@unistocks/shared/utils/enum-utils';
import { transactionTypeOptions } from '@unistocks/shared/utils/enums/transaction-options';
import { today } from '@unistocks/shared/utils/util';
import { FormProps } from '@unistocks/types/form-props';
import { forwardRef, memo } from 'react';
import { UseFormReturn } from 'react-hook-form';
import { z } from 'zod';

export type TransactionFilterFormProps = FormProps<TransactionFilter>;
export type TransactionFilterFormRef = UseFormReturn<TransactionFilter>;

const schemaFactory = () =>
  z.object({
    type: oneOf(
      transactionTypeOptions.map(option => option.value),
      t('::ValidationError:Required:Selection', { 0: t('::TransactionType') })
    ).nullish(),
    minDate: createDateSchema().nullish(),
    maxDate: createDateSchema({ max: today().toJSDate() }).nullish(),
    minAmount: createNumberSchema(),
    maxAmount: createNumberSchema()
  });

export const TransactionFilterForm = memo(
  forwardRef<TransactionFilterFormRef, TransactionFilterFormProps>(
    ({ initialValues, onSubmit, isSubmitting }, ref) => {
      const schema = schemaFactory();
      const todayDate = today().toISODate();

      return (
        <Form
          ref={ref}
          schema={schema}
          initialValues={initialValues}
          isLoading={isSubmitting}
          triggerText={t('::Apply')}
          triggerIcon={Filter}
          onSubmit={onSubmit}
        >
          <WatchValues<TransactionFilter> names={['minDate', 'maxDate']}>
            {([minDate, maxDate]) => (
              <>
                <Form.Select
                  name={'type'}
                  label={t('::TransactionType')}
                  placeholder={t('::All')}
                  options={translateEnumOptions(transactionTypeOptions)}
                />

                <Form.Datepicker
                  name={'minDate'}
                  label={t('::FromDate')}
                  maxDate={maxDate ?? todayDate}
                />
                <Form.Datepicker
                  name={'maxDate'}
                  label={t('::ToDate')}
                  minDate={minDate}
                  maxDate={todayDate}
                />

                <Form.Input name={'minAmount'} label={t('::MinAmount')} keyboardType={'numeric'} />
                <Form.Input name={'maxAmount'} label={t('::MaxAmount')} keyboardType={'numeric'} />
              </>
            )}
          </WatchValues>
        </Form>
      );
    }
  )
);

TransactionFilterForm.displayName = 'TransactionFilterForm';
